/**
 * TeamConsole — /team
 *
 * Enterprise-tier subscribers manage their team seats here. Owners can:
 *   • Create a team on first visit
 *   • Invite members by email (with a copyable join link)
 *   • Revoke pending invites
 *   • Remove active members
 *
 * Members see a read-only view of their team + who's on it.
 */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Users, UserPlus, Mail, Crown, Trash2, Copy, Check, Building2, Shield, Zap,
} from 'lucide-react';

const TeamConsole = () => {
  const { API, user } = useContext(AuthContext);
  const [data, setData] = useState(null); // { team, members, invites }
  const [credits, setCredits] = useState(null); // { pool, used, remaining, members }
  const [loading, setLoading] = useState(true);
  const [teamName, setTeamName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [copiedToken, setCopiedToken] = useState(null);

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/team/me`);
      setData(res);
      if (res?.team) {
        try {
          const { data: cu } = await axios.get(`${API}/team/credit-usage`);
          setCredits(cu);
        } catch (_) { /* silent */ }
      } else {
        setCredits(null);
      }
    } catch (err) {
      toast.error('Could not load team.');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const createTeam = async () => {
    if (!teamName.trim()) { toast.error('Team name required.'); return; }
    try {
      await axios.post(`${API}/team`, { name: teamName.trim(), plan: 'starter' });
      toast.success('Team created!');
      setTeamName('');
      fetchTeam();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create team.');
    }
  };

  const invite = async () => {
    if (!inviteEmail.trim()) { toast.error('Email required.'); return; }
    try {
      const { data: res } = await axios.post(`${API}/team/invite`, {
        email: inviteEmail.trim(),
        role:  inviteRole,
      });
      toast.success(`Invite sent to ${res.invite.email}`);
      setInviteEmail('');
      // Auto-copy join link so the owner can paste it into whatever channel they use.
      if (res.invite.join_url) {
        navigator.clipboard?.writeText(res.invite.join_url);
        toast.info('Join link copied to clipboard.');
      }
      fetchTeam();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send invite.');
    }
  };

  const revokeInvite = async (id) => {
    try {
      await axios.delete(`${API}/team/invite/${id}`);
      toast.success('Invite revoked.');
      fetchTeam();
    } catch (err) {
      toast.error('Could not revoke.');
    }
  };

  const removeMember = async (userId) => {
    if (!window.confirm('Remove this member from your team?')) return;
    try {
      await axios.delete(`${API}/team/member/${userId}`);
      toast.success('Member removed.');
      fetchTeam();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove member.');
    }
  };

  const copyLink = (url, token) => {
    navigator.clipboard?.writeText(url).then(() => {
      setCopiedToken(token);
      toast.success('Join link copied.');
      setTimeout(() => setCopiedToken(null), 1500);
    });
  };

  const isOwner = data?.team && data.team.owner_id === user?.id;
  const seatsUsed = (data?.members?.length || 0) + (data?.invites?.length || 0);
  const seatLimit = data?.team?.seat_limit || 0;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8" data-testid="team-console-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Enterprise</div>
            <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground leading-tight">
              Team seats
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Invite teammates onto your Enterprise subscription. All members
              get full course access + share the pooled AI credit budget.
            </p>
          </div>
        </div>

        {loading && <Card><CardContent className="p-6 text-sm text-muted-foreground">Loading…</CardContent></Card>}

        {/* -------- No team yet: create form -------- */}
        {!loading && !data?.team && (
          <Card data-testid="create-team-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Building2 className="w-4 h-4" /> Create your team</CardTitle>
              <CardDescription>
                One team per Enterprise subscription. You can rename it later.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="e.g. Peak & Prairie R&D"
                data-testid="team-name-input"
              />
              <Button
                onClick={createTeam}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="create-team-btn"
              >
                Create team
              </Button>
              <p className="text-[11px] text-muted-foreground pt-2">
                Team seats require an active Enterprise subscription. Upgrade from{' '}
                <a href="/pricing" className="underline font-bold">/pricing</a> if you&rsquo;re still on Free / Pro / Business.
              </p>
            </CardContent>
          </Card>
        )}

        {/* -------- Team exists -------- */}
        {!loading && data?.team && (
          <div className="space-y-4">
            {/* Header card */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-2xl">{data.team.name}</CardTitle>
                    <CardDescription>
                      Plan: <strong className="uppercase">{data.team.plan}</strong> ·
                      {' '}<strong>{seatsUsed}/{seatLimit}</strong> seats used ·
                      {' '}<strong>{data.team.credit_pool.toLocaleString()}</strong> pooled AI credits / month
                    </CardDescription>
                  </div>
                  {isOwner && (
                    <Badge className="bg-primary/10 text-primary uppercase tracking-widest text-[10px]" variant="outline">
                      <Crown className="w-3 h-3 mr-1" /> Owner
                    </Badge>
                  )}
                </div>
              </CardHeader>
            </Card>

            {/* AI credit pool meter */}
            {credits && (
              <Card data-testid="credit-meter-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-4 h-4" /> Pooled AI credits this month
                  </CardTitle>
                  <CardDescription>
                    <strong>{credits.used.toLocaleString()}</strong> of{' '}
                    <strong>{credits.pool.toLocaleString()}</strong> credits used ·
                    {' '}<strong>{credits.remaining.toLocaleString()}</strong> remaining ·
                    {' '}resets on the 1st
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Overall pool progress bar */}
                  <div className="w-full h-2.5 rounded-full bg-muted overflow-hidden" data-testid="pool-progress-bar">
                    <div
                      className={`h-full rounded-full transition-all ${
                        credits.pool > 0 && credits.used / credits.pool >= 0.9
                          ? 'bg-red-600'
                          : credits.pool > 0 && credits.used / credits.pool >= 0.7
                          ? 'bg-amber-500'
                          : 'bg-primary'
                      }`}
                      style={{
                        width: credits.pool > 0
                          ? `${Math.min(100, (credits.used / credits.pool) * 100)}%`
                          : '0%',
                      }}
                    />
                  </div>

                  {/* Per-member breakdown */}
                  {credits.members.length > 0 && (
                    <div className="pt-2 space-y-2" data-testid="member-usage-list">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                        Usage by member
                      </div>
                      {credits.members.map((m) => (
                        <div key={m.user_id} className="flex items-center gap-3" data-testid={`member-usage-${m.user_id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-heading font-bold text-foreground truncate flex items-center gap-1.5">
                              {m.email}
                              {m.is_owner && <Crown className="w-3 h-3 text-amber-500" />}
                              {m.user_id === user?.id && <Badge variant="outline" className="text-[9px]">you</Badge>}
                            </div>
                            <div className="mt-1 w-full h-1.5 rounded-full bg-muted overflow-hidden">
                              <div
                                className="h-full rounded-full bg-primary/70"
                                style={{
                                  width: credits.pool > 0
                                    ? `${Math.min(100, (m.used / credits.pool) * 100)}%`
                                    : '0%',
                                }}
                              />
                            </div>
                          </div>
                          <div className="text-right shrink-0 min-w-[100px]">
                            <div className="text-sm font-mono font-bold text-foreground">
                              {m.used.toLocaleString()}
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              {m.share_pct}% of pool
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Invite form — owner only */}
            {isOwner && (
              <Card data-testid="invite-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><UserPlus className="w-4 h-4" /> Invite a teammate</CardTitle>
                  <CardDescription>
                    We&rsquo;ll create a unique join link and copy it to your clipboard so you can share it.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col md:flex-row gap-2">
                  <Input
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="teammate@company.com"
                    type="email"
                    className="flex-1"
                    data-testid="invite-email-input"
                  />
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="h-10 px-3 text-sm rounded-md border border-input bg-background"
                    data-testid="invite-role-select"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                  <Button
                    onClick={invite}
                    disabled={seatsUsed >= seatLimit}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="send-invite-btn"
                  >
                    <Mail className="w-4 h-4 mr-1" /> Send invite
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Members list */}
            <Card>
              <CardHeader>
                <CardTitle>Members ({data.members.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {data.members.map((m) => (
                    <div key={m.id} className="px-4 py-3 flex items-center gap-3" data-testid={`member-row-${m.id}`}>
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-foreground">
                        {(m.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-heading font-extrabold text-foreground truncate flex items-center gap-2">
                          {m.email}
                          {m.is_owner && <Crown className="w-3.5 h-3.5 text-amber-500" />}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          <span className="uppercase">{m.team_role}</span>
                        </div>
                      </div>
                      {isOwner && !m.is_owner && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMember(m.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                          data-testid={`remove-member-${m.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Pending invites */}
            {isOwner && data.invites.length > 0 && (
              <Card data-testid="pending-invites-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Shield className="w-4 h-4" /> Pending invites ({data.invites.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {data.invites.map((inv) => {
                      const joinUrl = `${window.location.origin}/team/join/${inv.token}`;
                      return (
                        <div key={inv.id} className="px-4 py-3 flex items-center gap-3" data-testid={`invite-row-${inv.id}`}>
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-heading font-extrabold text-foreground truncate">{inv.email}</div>
                            <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-2">
                              <span className="uppercase">{inv.role}</span>
                              <span>·</span>
                              <span>invited {new Date(inv.created_at).toLocaleDateString()}</span>
                              <span>·</span>
                              <span>expires {new Date(inv.expires_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyLink(joinUrl, inv.token)}
                            data-testid={`copy-invite-${inv.id}`}
                          >
                            {copiedToken === inv.token ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                            Link
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => revokeInvite(inv.id)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`revoke-invite-${inv.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamConsole;
